import React, { useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { usePlatform } from '../context/PlatformContext';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import { parseExcelFile } from '../utils/excelParser';
import { generateCredentialId, generateVerificationId, extractInternId, ID_KEYS } from '../utils/idGenerator';
import { resolveTemplate, buildEmailData, DYNAMIC_VARIABLES } from '../utils/emailTemplates';
import {
  sendSingleCandidateEmail,
  sendBulkCandidateEmails,
  getBrevoStatus
} from '../utils/emailService';
import {
  downloadCertificateWithTemplate,
  generateCertificateWithTemplate
} from '../utils/pdfGenerator';
import { exportToZip } from '../utils/zipExporter';
import {
  FiArrowLeft, FiGrid, FiUsers, FiFileText, FiAward, FiMail,
  FiShield, FiBarChart2, FiSettings, FiPlus, FiUploadCloud,
  FiDownload, FiSend, FiRefreshCw, FiTrash2, FiCheckCircle,
  FiAlertCircle, FiCheck, FiX, FiClock, FiLayers, FiEdit3,
  FiArchive, FiCopy, FiShare2, FiExternalLink, FiFile
} from 'react-icons/fi';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_KEYS = ['name', 'full name', 'fullname', 'student name', 'candidate name', 'intern name', 'recipient'];
const EMAIL_KEYS = ['email', 'email address', 'e-mail', 'mail', 'emailid', 'email id'];

function findCol(headers, options) {
  return headers.find(h => options.includes(h.toLowerCase().trim())) || null;
}

export default function ProgramDetail() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const {
    programs,
    updateProgram,
    archiveProgram,
    reopenProgram,
    duplicateProgram,
    batches,
    addBatch,
    updateBatch,
    candidates,
    addCandidates,
    updateCandidate,
    deleteCandidate,
    deleteAllCandidates,
    emailHistory,
    addEmailToHistory,
    settings,
    customTemplatesMap,
    logAuditAction
  } = usePlatform();

  const program = useMemo(() => {
    return programs.find(p => p.id === programId) || programs[0];
  }, [programs, programId]);

  // Resolve the correct template URL:
  // 1. Custom template uploaded via Settings (stored in customTemplatesMap by type ID)
  // 2. Program-level templateUrl
  // 3. Fallback to default /template.png
  const resolvedTemplateUrl = useMemo(() => {
    const activeTypeId = settings?.template || 'offer_v2';
    const customTpl = customTemplatesMap?.[activeTypeId];
    if (customTpl?.url) return customTpl.url;
    if (program?.templateUrl && program.templateUrl !== '/template.png') return program.templateUrl;
    return '/template.png';
  }, [settings, customTemplatesMap, program]);

  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'candidates' | 'documents' | 'offers' | 'certificates' | 'emails' | 'verification' | 'analytics' | 'settings'
  const [selectedBatchId, setSelectedBatchId] = useState('all');

  // Program Batches & Candidates
  const programBatches = useMemo(() => {
    return batches.filter(b => b.programId === program?.id);
  }, [batches, program]);

  const programCandidates = useMemo(() => {
    return candidates.filter(c => {
      if (c.programId && c.programId !== program?.id) return false;
      if (selectedBatchId !== 'all' && c.batchId && c.batchId !== selectedBatchId) return false;
      return true;
    });
  }, [candidates, program, selectedBatchId]);

  // Program Email History
  const programEmailHistory = useMemo(() => {
    return emailHistory.filter(e => e.programId === program?.id || !e.programId);
  }, [emailHistory, program]);

  // Upload State
  const fileInputRef = useRef(null);
  const [previewRows, setPreviewRows] = useState(null);
  const [autoGenerateDocs, setAutoGenerateDocs] = useState(true);

  // Single Compose Modal
  const [composeModalCandidate, setComposeModalCandidate] = useState(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAttachPDF, setComposeAttachPDF] = useState(true);
  const [sendingSingle, setSendingSingle] = useState(false);

  // Bulk Dispatch Modal & Queue
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkPhase, setBulkPhase] = useState('confirm'); // 'confirm' | 'sending' | 'finished'
  const [bulkProgress, setBulkProgress] = useState({ total: 0, sent: 0, failed: 0, currentCandidate: null, results: [] });
  const cancellationRef = useRef({ isCancelled: false });

  // Next Batch Modal
  const [showNextBatchModal, setShowNextBatchModal] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchCode, setNewBatchCode] = useState('');

  if (!program) {
    return (
      <div className="p-12 text-center">
        <h2 className="text-xl font-bold text-slate-800">Program Not Found</h2>
        <Link to="/programs" className="text-sky-600 font-bold mt-2 inline-block">← Back to All Programs</Link>
      </div>
    );
  }

  const generatedCandidates = programCandidates.filter(c => c.docStatus === 'generated');
  const pendingEmailCandidates = generatedCandidates.filter(c => c.emailStatus !== 'sent');
  const sentCandidates = generatedCandidates.filter(c => c.emailStatus === 'sent');
  const failedCandidates = generatedCandidates.filter(c => c.emailStatus === 'failed');

  // ── CSV / EXCEL UPLOAD ─────────────────────────────────────────
  const processUploadedRows = (rawRows) => {
    const headers = Object.keys(rawRows[0] || {});
    const nameKey = findCol(headers, NAME_KEYS);
    const emailKey = findCol(headers, EMAIL_KEYS);

    if (!nameKey || !emailKey) {
      toast.error(`Could not find Name or Email columns. Found: ${headers.join(', ')}`);
      return null;
    }

    const existingEmails = new Set(programCandidates.map(c => c.email?.toLowerCase()));
    return rawRows
      .filter(r => r[nameKey]?.trim())
      .map(r => ({
        ...r,
        _name: r[nameKey]?.trim(),
        _email: r[emailKey]?.trim(),
        _internId: extractInternId(r, headers),
        _emailValid: EMAIL_REGEX.test(r[emailKey]?.trim() || ''),
        _isDuplicate: existingEmails.has(r[emailKey]?.toLowerCase().trim()),
      }));
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: ({ data }) => {
          const rows = processUploadedRows(data);
          if (rows) setPreviewRows(rows);
        },
      });
    } else if (['xlsx', 'xls'].includes(ext)) {
      toast.loading('Reading Excel...', { id: 'xl' });
      try {
        const data = await parseExcelFile(file);
        toast.dismiss('xl');
        const rows = processUploadedRows(data);
        if (rows) setPreviewRows(rows);
      } catch (e) {
        toast.error('Excel parse failed', { id: 'xl' });
      }
    } else {
      toast.error('Please upload a CSV or Excel file.');
    }
  };

  const confirmImport = () => {
    if (!previewRows) return;
    const valid = previewRows.filter(r => !r._isDuplicate && r._emailValid);
    const targetBatch = programBatches[0]?.id || null;
    const baseIdx = programCandidates.length;

    const newCands = valid.map((row, i) => {
      const uploadedId = row._internId || row.raw?._internId || null;
      const fallbackGenId = autoGenerateDocs
        ? `${program.idPrefix || 'IH'}-${String(program.year || 2026)}-${String(baseIdx + i + 1).padStart(4, '0')}`
        : null;
      const genId = uploadedId || fallbackGenId;

      return {
        id: crypto.randomUUID(),
        programId: program.id,
        batchId: targetBatch,
        name: row._name,
        email: row._email,
        college: row.College || row.college || row.Institution || '',
        department: row.Department || row.department || row.Branch || 'Technology',
        role: row.Role || row.role || program.domain || 'Intern',
        duration: row.Duration || row.duration || program.duration || '1 Month',
        startDate: row.StartDate || program.startDate,
        endDate: row.EndDate || program.endDate,
        candidateStatus: 'Selected',
        docStatus: genId ? 'generated' : 'pending',
        emailStatus: 'pending',
        certStatus: 'active',
        generatedId: genId,
        verificationId: genId || generateVerificationId('INT'),
        createdAt: new Date().toISOString(),
        mode: 'internship',
        raw: row,
      };
    });

    addCandidates(newCands, program.id, targetBatch);
    toast.success(`✅ Imported ${newCands.length} candidates into ${program.name}`);
    setPreviewRows(null);
  };

  // ── SINGLE SEND MODAL ───────────────────────────────────────────
  const openCompose = (candidate) => {
    const data = buildEmailData(candidate, settings, 'internship');
    const defaultSubject = settings.emailSubject || 'Your Internship Offer Letter — Innovation Hacks';
    const defaultBody = settings.emailBody || `Dear {{candidate_name}},\n\nCongratulations! 🥳 You have been selected for the Innovation Hacks Full Stack Development Internship.\n\n📎 Download your Official QR-Verified Offer Letter attached to this email.\n\nIntern Name: {{candidate_name}}\nIntern ID: {{internship_id}}\nInternship Duration: {{duration}}\n\nAfter downloading:\n\n1. Convert the Offer Letter into an image.\n2. Post it on LinkedIn.\n3. Tag Innovation Hacks in your post.\n\n🚀 After posting, stay active in the official communication channels to receive your task details and internship updates.\n\nWelcome to the Innovation Hacks Family! ❤️\n\nBuild. Innovate. Impact. 🌍\n\nBest Regards,\nKarthickkumar S\nFounder & CEO,\nInnovation Hacks\nwww.innovationhacks.in`;
    setComposeSubject(resolveTemplate(defaultSubject, data));
    setComposeBody(resolveTemplate(defaultBody, data));
    setComposeModalCandidate(candidate);
  };

  const handleSendSingle = async () => {
    if (!composeModalCandidate) return;
    setSendingSingle(true);
    const toastId = toast.loading(`Sending email to ${composeModalCandidate.name}...`);
    try {
      const res = await sendSingleCandidateEmail({
        candidate: composeModalCandidate,
        settings,
        mode: 'internship',
        templateUrl: resolvedTemplateUrl,
        customSubject: composeSubject,
        customBody: composeBody,
        attachPDF: composeAttachPDF,
      });

      toast.success(`✅ Email delivered to ${composeModalCandidate.name}!`, { id: toastId });
      updateCandidate(composeModalCandidate.id, {
        emailStatus: 'sent',
        brevoMessageId: res.messageId,
        lastEmailSentAt: res.sentAt,
      });
      addEmailToHistory({
        id: res.messageId || crypto.randomUUID(),
        programId: program.id,
        candidateId: composeModalCandidate.id,
        candidateName: composeModalCandidate.name,
        to: composeModalCandidate.email,
        subject: composeSubject,
        body: composeBody,
        attachPDF: composeAttachPDF,
        status: 'sent',
        sentAt: res.sentAt,
      });
      setComposeModalCandidate(null);
    } catch (e) {
      toast.error(`❌ Send failed: ${e.message}`, { id: toastId });
      updateCandidate(composeModalCandidate.id, { emailStatus: 'failed', lastError: e.message });
    } finally {
      setSendingSingle(false);
    }
  };

  // ── BULK SEND ───────────────────────────────────────────────────
  const startBulkDispatch = async () => {
    if (pendingEmailCandidates.length === 0) return toast.error('No pending emails to send.');
    setBulkPhase('sending');
    cancellationRef.current.isCancelled = false;

    // Process every result exactly once — prevents the off-by-one miss on
    // the last candidate when using only prog.results[last] on each callback.
    let lastProcessedIdx = 0;

    const applyNewResults = (results) => {
      for (let i = lastProcessedIdx; i < results.length; i++) {
        const latest = results[i];
        updateCandidate(latest.candidateId, {
          emailStatus: latest.status,
          brevoMessageId: latest.messageId || null,
          lastEmailSentAt: latest.sentAt,
        });
        addEmailToHistory({
          id: latest.messageId || crypto.randomUUID(),
          programId: program.id,
          candidateId: latest.candidateId,
          candidateName: latest.candidateName,
          to: latest.recipient,
          subject: latest.subject,
          body: latest.body,
          attachPDF: true,
          status: latest.status,
          errorMessage: latest.errorMessage,
          sentAt: latest.sentAt,
        });
      }
      lastProcessedIdx = results.length;
    };

    const res = await sendBulkCandidateEmails({
      candidates: pendingEmailCandidates,
      settings,
      mode: 'internship',
      templateUrl: resolvedTemplateUrl,
      cancellationToken: cancellationRef.current,
      onProgress: (prog) => {
        setBulkProgress(prog);
        applyNewResults(prog.results);
      },
    });

    setBulkPhase('finished');
    toast.success(`Batch complete: ${res.sent} sent, ${res.failed} failed.`);
  };


  // ── EXPORT DATA ─────────────────────────────────────────────────
  const exportProgramData = (format = 'csv') => {
    const exportRows = programCandidates.map(c => ({
      'Internship ID': c.generatedId || c.verificationId,
      'Name': c.name,
      'Email': c.email,
      'College': c.college,
      'Department': c.department,
      'Role': c.role,
      'Duration': c.duration,
      'Status': c.candidateStatus,
      'Email Status': c.emailStatus,
      'Brevo Message ID': c.brevoMessageId || 'N/A',
      'Created Date': c.createdAt,
    }));

    if (format === 'csv') {
      const csv = Papa.unparse(exportRows);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${program.name.replace(/\s+/g, '_')}_Candidates.csv`;
      a.click();
      toast.success('CSV Exported successfully!');
    } else {
      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Candidates');
      XLSX.writeFile(wb, `${program.name.replace(/\s+/g, '_')}_Candidates.xlsx`);
      toast.success('Excel Exported successfully!');
    }
  };

  // ── CREATE NEXT BATCH ───────────────────────────────────────────
  const handleCreateNextBatch = (e) => {
    e.preventDefault();
    if (!newBatchName.trim()) return toast.error('Enter batch name.');
    addBatch({
      programId: program.id,
      batchName: newBatchName,
      batchCode: newBatchCode || `BATCH-${Date.now().toString().slice(-4)}`,
      startDate: program.startDate,
      endDate: program.endDate,
      capacity: program.maximumSeats,
      status: 'Upcoming',
    });
    toast.success(`Batch "${newBatchName}" created for ${program.name}`);
    setShowNextBatchModal(false);
    setNewBatchName('');
    setNewBatchCode('');
  };

  const navTabs = [
    { id: 'overview', label: 'Overview', icon: FiGrid },
    { id: 'candidates', label: `Candidates (${programCandidates.length})`, icon: FiUsers },
    { id: 'documents', label: 'Documents', icon: FiFileText },
    { id: 'offers', label: 'Offer Letters', icon: FiFile },
    { id: 'certificates', label: 'Certificates', icon: FiAward },
    { id: 'emails', label: `Emails (${programEmailHistory.length})`, icon: FiMail },
    { id: 'verification', label: 'Verification', icon: FiShield },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
    { id: 'settings', label: 'Settings', icon: FiSettings },
  ];

  return (
    <div className="space-y-6 pb-16">
      {/* ── Breadcrumb & Top Bar ─────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/programs"
            className="p-2.5 bg-white border border-slate-200 text-slate-600 hover:text-sky-600 rounded-2xl shadow-sm hover:border-sky-300 transition-all flex items-center justify-center">
            <FiArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-100 rounded-full text-[10px] font-black uppercase">
                {program.categoryName || program.domain}
              </span>
              <span className="px-2.5 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-full text-[10px] font-black uppercase">
                ● {program.status}
              </span>
              {program.isArchived && (
                <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full text-[10px] font-black uppercase flex items-center gap-1">
                  <FiArchive className="w-3 h-3" /> Archived
                </span>
              )}
            </div>
            <h1 className="text-2xl font-semibold text-navy-900 tracking-tight mt-1">{program.name}</h1>
          </div>
        </div>

        {/* Quick Program Action Buttons */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => exportProgramData('csv')}
            className="btn-secondary text-xs">
            <FiDownload className="w-3.5 h-3.5" /> Export Data
          </button>
          <button
            onClick={() => setShowNextBatchModal(true)}
            className="btn-primary text-xs">
            <FiPlus className="w-3.5 h-3.5" /> + Create Next Batch
          </button>
        </div>
      </div>

      {/* ── 9 Sub-Tabs Navigation Bar ─────────────────────────────── */}
      <div className="flex gap-1 bg-surface-100 p-1.5 rounded-xl overflow-x-auto">
        {navTabs.map(t => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${isActive ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
              <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-brand-600' : 'text-slate-400'}`} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* ── TAB 1: OVERVIEW ───────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Executive Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 bg-white border border-slate-100">
              <div className="section-title mb-1">Total Enrolled</div>
              <div className="text-2xl font-bold text-navy-900">{programCandidates.length}</div>
              <div className="text-[11px] text-slate-700 font-semibold mt-1">Capacity: {program.maximumSeats} seats</div>
            </div>
            <div className="glass-card rounded-2xl p-5 bg-emerald-50/50 border border-emerald-100">
              <div className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Documents Generated</div>
              <div className="text-2xl font-bold text-emerald-700">{generatedCandidates.length}</div>
              <div className="text-[11px] text-emerald-600/80 mt-1">Offer letters & certificates ready</div>
            </div>
            <div className="glass-card rounded-2xl p-5 bg-brand-50/50 border border-sky-100">
              <div className="text-[10px] font-semibold text-brand-600 uppercase tracking-wider mb-1">Emails Dispatched</div>
              <div className="text-2xl font-bold text-sky-700">{sentCandidates.length}</div>
              <div className="text-[11px] text-sky-600/80 mt-1">{pendingEmailCandidates.length} pending dispatch</div>
            </div>
            <div className="glass-card rounded-2xl p-5 bg-purple-50/50 border border-purple-100">
              <div className="text-[10px] font-semibold text-violet-600 uppercase tracking-wider mb-1">Active Batches</div>
              <div className="text-2xl font-bold text-violet-700">{programBatches.length}</div>
              <div className="text-[11px] text-purple-600/80 mt-1">Current batch: {programBatches[0]?.batchName || 'Default'}</div>
            </div>
          </div>

          {/* Program Information Card */}
          <div className="glass-card rounded-2xl p-7 space-y-5">
            <h2 className="text-base font-semibold text-navy-900">Program Specifications & Rules</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-xs">
              <div className="p-4 bg-surface-50 rounded-xl border border-surface-200 space-y-2">
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Domain:</span> <div className="font-bold text-slate-900 mt-0.5">{program.domain}</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Duration:</span> <div className="font-bold text-slate-900 mt-0.5">{program.duration}</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Delivery Mode:</span> <div className="font-bold text-slate-900 mt-0.5">{program.mode} ({program.location})</div></div>
              </div>
              <div className="p-4 bg-surface-50 rounded-xl border border-surface-200 space-y-2">
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Timeline:</span> <div className="font-bold text-slate-900 mt-0.5">{program.startDate} → {program.endDate}</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Mentor Lead:</span> <div className="font-bold text-slate-900 mt-0.5">{program.mentor}</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">ID Prefix Format:</span> <div className="font-bold font-mono text-sky-600 mt-0.5">{program.idPrefix}-{program.year}-0001</div></div>
              </div>
              <div className="p-4 bg-surface-50 rounded-xl border border-surface-200 space-y-2">
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Organization:</span> <div className="font-semibold text-navy-900 mt-0.5">Innovation Hacks</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Sender Address:</span> <div className="font-mono text-sky-600 mt-0.5">innovationhacksofficial@gmail.com</div></div>
                <div><span className="text-slate-900 font-bold uppercase tracking-wider text-[10px]">Verification:</span> <div className="text-emerald-600 font-bold mt-0.5">✓ Inbuilt Lifetime QR Scan</div></div>
              </div>
            </div>
            {program.description && (
              <div className="p-4 bg-brand-50/50 rounded-2xl border border-sky-100 text-xs text-slate-600 leading-relaxed">
                <strong>Curriculum & Scope:</strong> {program.description}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 2: CANDIDATES ─────────────────────────────────────── */}
      {activeTab === 'candidates' && (
        <div className="space-y-5">
          {/* Action Bar */}
          <div className="glass-card rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-navy-900 text-sm">Enrolled Candidates ({programCandidates.length})</div>
              <div className="text-xs text-slate-800 font-semibold mt-0.5">{generatedCandidates.length} documents ready · {sentCandidates.length} emails sent</div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" ref={fileInputRef} onChange={e => handleFileUpload(e.target.files[0])} />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1.5 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:border-sky-400 hover:text-sky-600 transition-all shadow-sm">
                <FiUploadCloud className="w-4 h-4 text-sky-600" /> Import CSV / Excel
              </button>

              <button
                onClick={() => { setShowBulkModal(true); setBulkPhase('confirm'); }}
                disabled={pendingEmailCandidates.length === 0}
                className="flex items-center gap-2 px-5 py-2.5 bg-sky-600 text-white rounded-xl text-xs font-black hover:bg-sky-500 transition-all shadow-lg shadow-sky-500/20 disabled:opacity-50">
                <FiSend className="w-4 h-4" /> 🚀 SEND ALL ({pendingEmailCandidates.length})
              </button>

              {programCandidates.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm(`Delete all ${programCandidates.length} candidate(s) in this program?`)) {
                      deleteAllCandidates(program.id);
                      toast.success('Program candidate list cleared.');
                    }
                  }}
                  className="flex items-center gap-1.5 px-3.5 py-2.5 bg-rose-50 text-rose-600 border border-rose-100 rounded-xl text-xs font-bold hover:bg-rose-100 transition-all">
                  <FiTrash2 className="w-3.5 h-3.5" /> Delete All ({programCandidates.length})
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="glass-card rounded-2xl overflow-hidden">
            {programCandidates.length === 0 ? (
              <div className="p-12 text-center">
                <div className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center text-sky-600 mx-auto mb-3">
                  <FiUploadCloud className="w-6 h-6" />
                </div>
                <div className="font-bold text-slate-800 text-sm">No Candidates Enrolled Yet</div>
                <p className="text-xs text-slate-800 font-semibold mt-1 max-w-sm mx-auto">Upload a CSV or Excel dataset with Name & Email columns to enroll candidates into this program.</p>
                <button onClick={() => fileInputRef.current?.click()} className="mt-4 px-5 py-2 bg-sky-600 text-white rounded-xl text-xs font-bold hover:bg-sky-500 shadow-md">
                  Upload Dataset
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-surface-100">
                    <tr>
                      {['#', 'Candidate', 'Email', 'Credential ID', 'Status', 'Email Status', 'Action'].map(h => (
                        <th key={h} className="px-5 py-3 text-left font-black text-slate-400 uppercase tracking-widest text-[10px]">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {programCandidates.map((c, i) => (
                      <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3.5 text-xs font-bold text-slate-400">{i + 1}</td>
                        <td className="px-5 py-3.5">
                          <div className="font-bold text-slate-900">{c.name}</div>
                          {c.college && <div className="text-[10px] text-slate-700 font-bold">{c.college}</div>}
                        </td>
                        <td className="px-5 py-3.5 font-mono text-xs text-slate-600">{c.email}</td>
                        <td className="px-5 py-3.5">
                          <code className="text-xs font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded border border-sky-100">
                            {c.generatedId || c.verificationId}
                          </code>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded-full text-[10px] font-bold">
                            {c.candidateStatus || 'Selected'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={c.emailStatus || 'pending'} />
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openCompose(c)}
                              className="px-3 py-1.5 bg-sky-600 text-white rounded-xl text-xs font-bold hover:bg-sky-500 transition-all flex items-center gap-1 shadow-sm">
                              <FiMail className="w-3 h-3" /> {c.emailStatus === 'sent' ? 'Resend' : 'Send Mail'}
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(`Delete candidate "${c.name}"?`)) deleteCandidate(c.id);
                              }}
                              className="p-1.5 text-slate-300 hover:text-rose-600 rounded-lg">
                              <FiTrash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 3: DOCUMENTS ──────────────────────────────────────── */}
      {activeTab === 'documents' && (
        <div className="space-y-5">
          <div className="glass-card rounded-2xl p-6 flex justify-between items-center flex-wrap gap-4">
            <div>
              <div className="font-semibold text-navy-900 text-sm">Program Documents & Certificates</div>
              <div className="text-xs text-slate-800 font-semibold mt-0.5">{generatedCandidates.length} generated documents linked to this program</div>
            </div>
            <button
              onClick={() => exportToZip(generatedCandidates, settings, 'internship', resolvedTemplateUrl)}
              disabled={generatedCandidates.length === 0}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 disabled:opacity-50">
              <FiDownload className="w-4 h-4" /> Download All PDFs (ZIP)
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedCandidates.map(c => (
              <div key={c.id} className="glass-card rounded-2xl p-4 flex items-center justify-between border border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center text-sky-600 flex-shrink-0">
                    <FiFileText className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-900 text-xs">{c.name}</div>
                    <div className="text-[10px] text-slate-700 font-bold font-mono">{c.generatedId || c.verificationId} · Offer Letter PDF</div>
                  </div>
                </div>
                <button
                  onClick={() => downloadCertificateWithTemplate(c, resolvedTemplateUrl, settings, 'internship')}
                  className="p-2 bg-slate-100 hover:bg-sky-50 text-slate-600 hover:text-sky-600 rounded-xl text-xs font-bold transition-all flex items-center gap-1">
                  <FiDownload className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB 4: OFFER LETTERS ──────────────────────────────────── */}
      {activeTab === 'offers' && (
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-semibold text-navy-900">Offer Letter Dispatch Hub</h2>
              <p className="text-xs text-slate-900 font-bold">Innovation Hacks calibrated layout with auto QR verification.</p>
            </div>
            <button
              onClick={() => { setShowBulkModal(true); setBulkPhase('confirm'); }}
              disabled={pendingEmailCandidates.length === 0}
              className="flex items-center gap-2 px-6 py-2.5 bg-sky-600 text-white rounded-xl text-xs font-black hover:bg-sky-500 shadow-md shadow-sky-500/20 disabled:opacity-50">
              <FiSend className="w-4 h-4" /> Dispatch All Offer Letters ({pendingEmailCandidates.length})
            </button>
          </div>

          <div className="p-4 bg-sky-50 rounded-2xl border border-sky-100 text-xs text-sky-800 space-y-1">
            <div><strong>Personalization Guarantee:</strong> Each candidate receives their distinct offer letter PDF with unique ID & QR code.</div>
            <div><strong>Sender Identity:</strong> Innovation Hacks &lt;innovationhacksofficial@gmail.com&gt; via Brevo REST API v3.</div>
          </div>
        </div>
      )}

      {/* ── TAB 5: CERTIFICATES ───────────────────────────────────── */}
      {activeTab === 'certificates' && (
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-semibold text-navy-900">Completion Certificate Issuance</h2>
              <p className="text-xs text-slate-900 font-bold">Inbuilt Lifetime validity · QR Code Verification enabled.</p>
            </div>
            <span className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl text-xs font-bold">
              ✓ Inbuilt Lifetime Validity
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedCandidates.map(c => (
              <div key={c.id} className="p-4 bg-surface-50 rounded-xl border border-surface-200 flex items-center justify-between">
                <div>
                  <div className="font-bold text-slate-900 text-xs">{c.name}</div>
                  <div className="text-[10px] text-slate-700 font-bold font-mono">{c.generatedId || c.verificationId} · Permanent Active</div>
                </div>
                <button
                  onClick={() => downloadCertificateWithTemplate(c, resolvedTemplateUrl, settings, 'certificate')}
                  className="px-3.5 py-1.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:border-sky-400 flex items-center gap-1.5">
                  <FiAward className="w-3.5 h-3.5 text-sky-600" /> Certificate PDF
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB 6: EMAILS ─────────────────────────────────────────── */}
      {activeTab === 'emails' && (
        <div className="glass-card rounded-2xl overflow-hidden">
          <div className="p-5 border-b border-surface-100 flex justify-between items-center">
            <span className="font-semibold text-navy-900 text-sm">Dispatched Email Records</span>
            <span className="text-xs text-slate-800 font-semibold font-normal">{programEmailHistory.length} records</span>
          </div>
          {programEmailHistory.length === 0 ? (
            <div className="p-12 text-center text-xs text-slate-900 font-bold">No emails dispatched for this program yet.</div>
          ) : (
            <div className="divide-y divide-slate-50">
              {programEmailHistory.map(e => (
                <div key={e.id} className="p-4 flex items-center justify-between text-xs hover:bg-slate-50/50">
                  <div>
                    <div className="font-bold text-slate-900">{e.subject}</div>
                    <div className="text-[11px] text-slate-700 font-semibold mt-0.5">To: <strong className="text-slate-600">{e.to}</strong> · {e.sentAt ? new Date(e.sentAt).toLocaleString() : 'Recent'}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={e.status} />
                    <code className="text-[10px] font-mono text-sky-700 bg-sky-50 px-2 py-0.5 rounded">{e.id?.substring(0, 14)}...</code>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB 7: VERIFICATION ───────────────────────────────────── */}
      {activeTab === 'verification' && (
        <div className="glass-card rounded-2xl p-8 space-y-6">
          <h2 className="text-base font-semibold text-navy-900">Live Public Verification Ledger</h2>
          <p className="text-xs text-slate-800 font-semibold leading-relaxed">
            Every candidate document contains a high-resolution QR code pointing to <code className="text-sky-600 font-mono">/#/verify/&lt;CREDENTIAL_ID&gt;</code>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedCandidates.slice(0, 6).map(c => {
              const vId = c.verificationId || c.generatedId;
              return (
                <div key={c.id} className="p-4 bg-surface-50 rounded-xl border border-surface-200 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-slate-900">{c.name}</div>
                    <code className="text-[10px] font-mono text-sky-600">{vId}</code>
                  </div>
                  <Link
                    to={`/verify/${vId}`}
                    target="_blank"
                    className="flex items-center gap-1 text-sky-600 hover:text-sky-700 font-bold text-[11px]">
                    <span>Test Scan</span>
                    <FiExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── TAB 8: ANALYTICS ──────────────────────────────────────── */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-5 text-center bg-white border border-slate-100">
              <div className="text-[10px] font-black text-slate-400 uppercase mb-1">Enrollment Rate</div>
              <div className="text-2xl font-semibold text-navy-900">
                {Math.round((programCandidates.length / (program.maximumSeats || 200)) * 100)}%
              </div>
            </div>
            <div className="glass-card rounded-2xl p-5 text-center bg-emerald-50/50 border border-emerald-100">
              <div className="text-[10px] font-black text-emerald-600 uppercase mb-1">Doc Gen Rate</div>
              <div className="text-2xl font-black text-emerald-700">
                {programCandidates.length ? Math.round((generatedCandidates.length / programCandidates.length) * 100) : 0}%
              </div>
            </div>
            <div className="glass-card rounded-2xl p-5 text-center bg-brand-50/50 border border-sky-100">
              <div className="text-[10px] font-semibold text-brand-600 uppercase mb-1">Email Delivery</div>
              <div className="text-2xl font-black text-sky-700">
                {generatedCandidates.length ? Math.round((sentCandidates.length / generatedCandidates.length) * 100) : 0}%
              </div>
            </div>
            <div className="glass-card rounded-2xl p-5 text-center bg-purple-50/50 border border-purple-100">
              <div className="text-[10px] font-black text-purple-600 uppercase mb-1">Verification Reliability</div>
              <div className="text-2xl font-black text-purple-700">100%</div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 9: SETTINGS ───────────────────────────────────────── */}
      {activeTab === 'settings' && (
        <div className="glass-card rounded-2xl p-8 space-y-6 text-xs">
          <div className="flex justify-between items-center border-b border-surface-100 pb-4">
            <div>
              <h2 className="text-base font-semibold text-navy-900">Program Configuration & Lifecycle</h2>
              <p className="text-slate-400 mt-0.5">Control program status, archiving, and duplicates.</p>
            </div>
            {program.isArchived ? (
              <button
                onClick={() => {
                  reopenProgram(program.id);
                  toast.success(`Program "${program.name}" reopened!`);
                }}
                className="px-5 py-2 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-500 shadow-md">
                Reopen Program
              </button>
            ) : (
              <button
                onClick={() => {
                  if (confirm(`Archive "${program.name}"? Records remain accessible anytime.`)) {
                    archiveProgram(program.id);
                    toast.success('Program archived.');
                  }
                }}
                className="px-5 py-2 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-500 shadow-md">
                Archive Program
              </button>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block section-title mb-1">Program Status Override</label>
              <select
                value={program.status}
                onChange={e => updateProgram(program.id, { status: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 font-bold focus:outline-none focus:border-sky-400">
                {['Draft', 'Upcoming', 'Application Open', 'Active', 'Completed', 'Paused', 'Archived'].map(st => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block section-title mb-1">Credential ID Prefix</label>
              <input
                value={program.idPrefix || 'IH-FS'}
                onChange={e => updateProgram(program.id, { idPrefix: e.target.value })}
                className="w-full border border-slate-200 rounded-xl px-3 py-2 font-mono font-bold focus:outline-none focus:border-sky-400"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── IMPORT PREVIEW MODAL ─────────────────────────────────── */}
      <AnimatePresence>
        {previewRows && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setPreviewRows(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="relative bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100 bg-surface-50">
                <h3 className="font-semibold text-navy-900 text-sm">Import Candidates into {program.name}</h3>
                <button onClick={() => setPreviewRows(null)} className="p-1.5 text-slate-400 hover:text-slate-700">
                  <FiX className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-4 text-xs">
                <div className="flex gap-2">
                  <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded-xl font-bold">
                    ✓ {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Valid
                  </span>
                  <span className="px-3 py-1 bg-amber-50 text-amber-700 border border-amber-100 rounded-xl font-bold">
                    ⚠️ {previewRows.filter(r => r._isDuplicate).length} Duplicates
                  </span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-sky-50 rounded-xl border border-sky-100">
                  <input type="checkbox" id="auto-gen-prog" checked={autoGenerateDocs} onChange={e => setAutoGenerateDocs(e.target.checked)} className="w-4 h-4 accent-sky-600" />
                  <label htmlFor="auto-gen-prog" className="font-bold text-slate-700 cursor-pointer">Auto generate unique IDs ({program.idPrefix}-2026-0001)</label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setPreviewRows(null)} className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
                  <button onClick={confirmImport} className="px-6 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-500 shadow-md">
                    Import {previewRows.filter(r => !r._isDuplicate && r._emailValid).length} Candidates
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── SINGLE COMPOSE MODAL ─────────────────────────────────── */}
      <Modal
        isOpen={!!composeModalCandidate}
        onClose={() => setComposeModalCandidate(null)}
        title={`Send Document — ${composeModalCandidate?.name}`}
        size="md">
        {composeModalCandidate && (
          <div className="p-6 space-y-4 text-xs">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex justify-between items-center">
              <div><strong className="text-slate-700">To:</strong> {composeModalCandidate.email}</div>
              <code className="font-mono text-sky-600 bg-sky-50 px-2 py-0.5 rounded">{composeModalCandidate.generatedId || composeModalCandidate.verificationId}</code>
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Subject</label>
              <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} className="w-full border border-slate-200 rounded-xl px-3.5 py-2 focus:outline-none focus:border-sky-400 font-medium" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Message Body</label>
              <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} rows={7} className="w-full border border-slate-200 rounded-xl px-3.5 py-2 focus:outline-none focus:border-sky-400 font-mono resize-none" />
            </div>
            <div className="flex items-center gap-2 p-2.5 bg-sky-50 rounded-xl border border-sky-100">
              <input type="checkbox" id="attach-pdf-prog" checked={composeAttachPDF} onChange={e => setComposeAttachPDF(e.target.checked)} className="w-4 h-4 accent-sky-600" />
              <label htmlFor="attach-pdf-prog" className="font-bold text-slate-700">Attach Personalized PDF Document</label>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setComposeModalCandidate(null)} className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
              <button onClick={handleSendSingle} disabled={sendingSingle} className="px-6 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-500 flex items-center gap-1.5 disabled:opacity-50">
                {sendingSingle ? <FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FiSend className="w-3.5 h-3.5" />} Send via Brevo
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── BULK DISPATCH MODAL ──────────────────────────────────── */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={bulkPhase === 'sending' ? undefined : () => setShowBulkModal(false)} />
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-surface-100 pb-3">
              <h3 className="font-semibold text-navy-900 text-sm">Bulk Dispatch — {program.name}</h3>
              {bulkPhase !== 'sending' && <button onClick={() => setShowBulkModal(false)}><FiX className="w-4 h-4" /></button>}
            </div>

            {bulkPhase === 'confirm' && (
              <div className="space-y-4 text-xs">
                <p className="text-slate-600">Ready to dispatch emails & personalized PDFs to <strong className="text-slate-900">{pendingEmailCandidates.length}</strong> candidates via Brevo REST API.</p>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowBulkModal(false)} className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
                  <button onClick={startBulkDispatch} className="px-6 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-500 shadow-md">Confirm & Start</button>
                </div>
              </div>
            )}

            {bulkPhase === 'sending' && (
              <div className="space-y-4 text-xs text-center py-2">
                <FiRefreshCw className="w-8 h-8 text-sky-600 animate-spin mx-auto" />
                <div className="font-semibold text-navy-900 text-sm">Sending: {bulkProgress.sent + bulkProgress.failed} / {bulkProgress.total}</div>
                {bulkProgress.currentCandidate && (
                  <div className="text-slate-500 text-[11px] truncate">Recipient: {bulkProgress.currentCandidate.email}</div>
                )}
                <button onClick={() => { cancellationRef.current.isCancelled = true; }} className="px-4 py-1.5 bg-rose-50 text-rose-600 border border-rose-100 rounded-xl font-bold text-[11px]">
                  Stop Dispatch
                </button>
              </div>
            )}

            {bulkPhase === 'finished' && (
              <div className="space-y-4 text-center py-2 text-xs">
                <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto"><FiCheck className="w-5 h-5" /></div>
                <div className="font-semibold text-navy-900 text-sm">Dispatch Completed</div>
                <p className="text-slate-500">{bulkProgress.sent} sent · {bulkProgress.failed} failed</p>
                <button onClick={() => setShowBulkModal(false)} className="px-6 py-2 bg-slate-900 text-white rounded-xl font-bold">Done</button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* ── CREATE NEXT BATCH MODAL ──────────────────────────────── */}
      <Modal
        isOpen={showNextBatchModal}
        onClose={() => setShowNextBatchModal(false)}
        title={`Create Next Batch for ${program.name}`}
        size="sm">
        <form onSubmit={handleCreateNextBatch} className="p-6 space-y-4 text-xs">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Batch Name *</label>
            <input
              required
              placeholder="e.g. October 2026 Batch"
              value={newBatchName}
              onChange={e => setNewBatchName(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 font-bold focus:outline-none focus:border-sky-400"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">Batch Code</label>
            <input
              placeholder="e.g. AI-OCT-26"
              value={newBatchCode}
              onChange={e => setNewBatchCode(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 font-mono focus:outline-none focus:border-sky-400"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowNextBatchModal(false)} className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl font-bold">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-500 shadow-md">Create Batch</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
